import { filterArrayEmpty, urlAddQueries } from 'lib/riskbase';
import { BlockManagerSendCommunicationRequest } from 'types/DTOs/BlockManagerDTOs';
import { FilesContext, FilesContextEnum, UID, UUID } from 'types/types';

const PHPAPI = process.env.EXPO_PUBLIC_PHPAPI || 'https://api.riskbase.uk';
const NODEAPI = process.env.EXPO_PUBLIC_NODEAPI || 'https://b1.riskbase.uk';
const APPAPI = process.env.EXPO_PUBLIC_APPAPI || 'https://app-api.riskbase.uk';

export enum Api {
  PHP = 'PHP',
  NODE = 'NODE',
  APP = 'APP',
}

export const ApiUrl: Record<Api, string> = {
  [Api.PHP]: PHPAPI,
  [Api.NODE]: NODEAPI,
  [Api.APP]: APPAPI,
};

const API_URL_AUTH = `${PHPAPI}/auth`;

const engageUrls = ['https://rskbs.uk/'];
export function isEngageUrl(url: string) {
  return engageUrls.some((engageUrl) => url.startsWith(engageUrl));
}

export const apiAppUpdateAvailable = `${PHPAPI}/appupdateavailable`;

// AUTH
export const AUTH_EMAIL = `${API_URL_AUTH}/email`;
export const AUTH_EMAIL_VALIDATE = `${API_URL_AUTH}/email/validate`;
export const AUTH_PASSWORD = `${API_URL_AUTH}/password`;
export const AUTH_REQUEST_PASSWORD_RESET = `${API_URL_AUTH}/password/request`;
export const AUTH_SET_PASSWORD = `${API_URL_AUTH}/password/set`;
export const AUTH_SIGN_OUT = `${API_URL_AUTH}`;
export const AUTH_TFA_SETUP = `${API_URL_AUTH}/tfa/set`;
export const AUTH_TFA = `${API_URL_AUTH}/tfa`;

// GENERAL
export const ASSESSMENT_PDF = (assessmentId: string): string =>
  `${PHPAPI}/assessments/${assessmentId}/report`;
export const ASSESSMENT_LIVE_PDF = (assessmentId: string): string =>
  `${PHPAPI}/assessments/${assessmentId}/livereport`;
export const aseElementPdf = (assessmentId: string, elementId: UUID): string =>
  `${PHPAPI}/assessments/${assessmentId}/elementreport/${elementId}`;
export const aseElementPdfsZip = (assessmentId: string): string =>
  `${PHPAPI}/assessments/${assessmentId}/elementreports`;
export const aseElementBundleZip = (assessmentId: string): string =>
  `${PHPAPI}/assessments/${assessmentId}/bundle`;
export const SEARCH_PREFLIGHT = `${PHPAPI}/assets/search/data`;
export const SEARCH = `${PHPAPI}/assets/search`;
export const SYNC = `${PHPAPI}/assets/sync`;
export const THUMBNAILS = `${PHPAPI}/files/getthumbnails`;
export const FILES = `${PHPAPI}/files/getfiles`;
export const ASSET_AVATAR = (assetId: string, cacheKey?: string): string =>
  `${PHPAPI}/assets/${assetId}/avatar.jpg${cacheKey ? `?_nc=${cacheKey}` : ''}`;
export const UPLOAD_FILE = (fileId: string): string =>
  `${PHPAPI}/filessafe/${fileId}`;
export const PING = `${PHPAPI}/ping`;
export const ERRORS = `${PHPAPI}/errors`;

export const NETWORK_ERROR = 'NetworkError';
export const UNAUTHORIZED = 'NotAuthorized';
export const OFFLINE = 'Offline';

export const CREATE_ASSET = `${PHPAPI}/create/asset`;

export const API_ACCOUNT_AVATAR = (): string => `${PHPAPI}/account/avatar`;

export const API_FILE_GENERIC = (
  filename: string,
  download?: boolean,
): string =>
  `${PHPAPI}/files/generic/${filename}${download ? '?download=1' : ''}`;

export const apiFileGenericPoll = (filename: string): string =>
  `${PHPAPI}/files/generic/${filename}/poll`;

// NEW API

export const API_ACCOUNT_DETAILS = (): string => `${NODEAPI}/account/details`;

export const API_ACCOUNT_TFA = (): string => `${NODEAPI}/account/tfa`;

export const API_ACCOUNT_EMAIL_CHANGE = (): string =>
  `${NODEAPI}/account/email/change`;

export const API_ACCOUNT_PASSWORD_CHANGE = (): string =>
  `${NODEAPI}/account/password/change`;

export const API_ORGANISATION_LIST = (): string => `${NODEAPI}/organisations`;

export const API_ORGANISATION = (organisationId: string): string =>
  `${NODEAPI}/organisation/${organisationId}`;

export const API_ORGANISATION_SETTINGS_FIELDS = (
  organisationId: string,
): string => `${NODEAPI}/organisation/${organisationId}/settings/fields`;

export const API_ORGANISATION_SETTINGS_FIELDS_RESULT_COUNT = (
  organisationId: string,
): string =>
  `${NODEAPI}/organisation/${organisationId}/settings/fields/resultscount`;

export const API_ORGANISATION_SETTINGS_FIELDS_SECTION = (
  organisationId: string,
  sectionId?: string | undefined | null,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/organisation/${organisationId}` +
  `/settings/fields/section${sectionId ? `/${sectionId}` : ''}${
    sectionId && doDelete ? '/delete' : ''
  }`;

export const API_ORGANISATION_SETTINGS_FIELDS_CLIENT = (
  organisationId: string,
  sectionId: string,
  clientId?: string,
  additionalAction?: 'delete' | 'unlink',
): string =>
  `${NODEAPI}/organisation/${organisationId}` +
  `/settings/fields/section/${sectionId}` +
  `/client${clientId ? `/${clientId}` : ''}${
    clientId && additionalAction ? `/${additionalAction}` : ''
  }`;

export const API_ORGANISATION_SETTINGS_FIELDS_GROUP = (
  organisationId: string,
  groupId?: string | undefined | null,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/organisation/${organisationId}` +
  `/settings/fields/group${groupId ? `/${groupId}` : ''}` +
  `${groupId && doDelete ? '/delete' : ''}`;

export const API_ORGANISATION_SETTINGS_FIELDS_GROUP_ADD_GROUP = (
  organisationId: string,
  groupId: string,
): string =>
  `${NODEAPI}/organisation/${organisationId}` +
  `/settings/fields/group${groupId ? `/${groupId}` : ''}` +
  '/addgroup';

export const API_ORGANISATION_SETTINGS_FIELDS_FIELD = (
  organisationId: string,
  fieldId?: string | undefined | null,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/organisation/${organisationId}` +
  `/settings/fields/field${fieldId ? `/${fieldId}` : ''}${
    fieldId && doDelete ? '/delete' : ''
  }`;

// Assessment Settings Template

export const API_ASESETUP_TEMPLATES = (organisationId: string): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/templates`;

export const urlSettingsAssessmentChecks = (organisationId: string): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/checks`;

export const API_ASESETUP_TEMPLATE = (
  organisationId: string,
  templateId?: number,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/template` +
  `${templateId ? `/${templateId}` : ''}${
    templateId && doDelete ? '/delete' : ''
  }`;

export const apiAseSetupFlag = (
  organisationId: string,
  flagId?: UID,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/flag` +
  `${flagId ? `/${flagId}` : ''}${flagId && doDelete ? '/delete' : ''}`;

export const apiAseSetupSignatory = (
  organisationId: UID,
  signatoryId?: UID,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/signatory` +
  `${signatoryId ? `/${signatoryId}` : ''}${signatoryId && doDelete ? '/delete' : ''}`;

export const apiAseSetupSignatoryOrder = (organisationId: string): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/signatory/order`;

// Assessment Settings Library

export const API_ASESETUP_LIBRARY = (organisationId: string): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/library`;

export const API_ASESETUP_FOLDER = (
  organisationId: string,
  folderId?: number,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/folder` +
  `${folderId ? `/${folderId}` : ''}${folderId && doDelete ? '/delete' : ''}`;

export const API_ASESETUP_FOLDERSORDER = (organisationId: string): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/folders/order`;

export const API_ASESETUP_SECTIONSORDER = (organisationId: string): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/sections/order`;

export const API_ASESETUP_QUESTIONSORDER = (organisationId: string): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/questions/order`;

export const API_ASESETUP_SECTION = (
  organisationId: string,
  sectionId?: number,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/section` +
  `${sectionId ? `/${sectionId}` : ''}${
    sectionId && doDelete ? '/delete' : ''
  }`;

export const API_ASESETUP_QUESTION = (
  organisationId: string,
  questionId?: number,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/question` +
  `${questionId ? `/${questionId}` : ''}${
    questionId && doDelete ? '/delete' : ''
  }`;

export const API_ASESETUP_ANSWER = (
  organisationId: string,
  answerId?: number,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/answer` +
  `${answerId ? `/${answerId}` : ''}${answerId && doDelete ? '/delete' : ''}`;

export const API_ASESETUP_CHECKLISTITEM = (
  organisationId: string,
  checklistItemId?: number,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/organisation/${organisationId}/setup/assessments/checklistitem` +
  `${checklistItemId ? `/${checklistItemId}` : ''}${
    checklistItemId && doDelete ? '/delete' : ''
  }`;

export const API_ASESETUP_ACTION = (
  organisationId: string,
  actionId?: number | undefined | null,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/organisation/${organisationId}` +
  `/setup/assessments/action${actionId ? `/${actionId}` : ''}${
    actionId && doDelete ? '/delete' : ''
  }`;

export const API_ASESETUP_CONTROL = (
  organisationId: string,
  controlId?: number | undefined | null,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/organisation/${organisationId}` +
  `/setup/assessments/control${controlId ? `/${controlId}` : ''}${
    controlId && doDelete ? '/delete' : ''
  }`;

export const apiSettingsBillingNode = (organisationId: UID): string =>
  `${NODEAPI}/billing/${organisationId}`;
export const apiSettingsBillingProduct = (
  organisationId: UID,
  productId?: UID,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/billing/${organisationId}/product` +
  `${productId ? `/${productId}` : ''}` +
  `${productId && doDelete ? '/delete' : ''}`;
export const apiSettingsBillingProductUnits = (
  organisationId: UID,
  productId: UID,
): string => `${NODEAPI}/billing/${organisationId}/product/${productId}/units`;
export const apiSettingsBillingItem = (
  organisationId: UID,
  itemId?: string,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/billing/${organisationId}/item` +
  `${itemId ? `/${itemId}` : ''}` +
  `${itemId && doDelete ? '/delete' : ''}`;
export const apiSettingsBillingDiscount = (
  organisationId: UID,
  discountId?: string,
  doDelete?: boolean,
): string =>
  `${NODEAPI}/billing/${organisationId}/discount` +
  `${discountId ? `/${discountId}` : ''}` +
  `${discountId && doDelete ? '/delete' : ''}`;
export const apiSettingsBillingPhp = (organisationId: UID): string =>
  `${PHPAPI}/billing/${organisationId}`;

export const apiSetupBillingInvoicePdf = (
  organisationId: UID,
  invoiceReference: string,
): string =>
  `${PHPAPI}/billing/${organisationId}/invoice/${invoiceReference}/pdf`;
export const apiSetupBillingInvoiceCsv = (
  organisationId: UID,
  invoiceReference: string,
): string =>
  `${PHPAPI}/billing/${organisationId}/invoice/${invoiceReference}/csv`;
export const apiSetupBillingStatementPdf = (organisationId: UID): string =>
  `${PHPAPI}/billing/${organisationId}/statement/pdf`;
export const apiSetupBillingStatementCsv = (organisationId: UID): string =>
  `${PHPAPI}/billing/${organisationId}/statement/csv`;
export const apiSetupBillingDraftPdf = (
  organisationId: UID,
  invoiceCode: string,
  date: number,
): string =>
  `${PHPAPI}/billing/${organisationId}/draftinvoice/${invoiceCode}:${date}/pdf`;
export const apiSetupBillingDraftCsv = (
  organisationId: UID,
  invoiceCode: string,
  date: number,
): string =>
  `${PHPAPI}/billing/${organisationId}/draftinvoice/${invoiceCode}:${date}/csv`;
export const apiSetupBillingCreditNotePdf = (
  organisationId: UID,
  creditNoteReference: string,
): string =>
  `${PHPAPI}/billing/${organisationId}/creditnote/${creditNoteReference}/pdf`;

// Assessment QA

export const apiAsesList = (): string => `${NODEAPI}/assessments/list`;

export const apiAsesListFilters = (): string =>
  `${NODEAPI}/assessments/listfilters`;

export const apiAsesAssessment = (assessmentId: UID): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}`;

export type APIRemoteUrl<T extends string = string> = {
  (fileId: T): string;
  (fileId?: undefined): undefined;
  (fileId: T | undefined): string | undefined;
};

export const API_ASE_IMAGE = (
  assessmentId: UID,
  options: {
    size?: 'xsc' | 's' | 'l' | 'xl';
    urlToken?: string;
  },
) =>
  ((imageId?: UUID | undefined) =>
    imageId !== undefined
      ? urlAddQueries(
          `${PHPAPI}/ases/assessment/${assessmentId}/image/${imageId || ''}`,
          [
            options.size ? `size=${options.size}` : null,
            options.urlToken ? `ut=${options.urlToken}` : null,
          ].filter(filterArrayEmpty),
        )
      : undefined) as {
    (imageId: UUID): string;
    (imageId?: undefined): undefined;
    (imageId: UUID | undefined): string | undefined;
  };

export const API_ASE_UNSUBMIT = (assessmentId: UID): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/unsubmit`;

export const API_ASE_APPROVE = (assessmentId: UID): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/approve`;

export const API_ASE_UNAPPROVE = (assessmentId: UID): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/unapprove`;

export const apiAseSaveSummary = (assessmentId: UID): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/summary`;

export const apiAseSaveAssetDetails = (assessmentId: UID): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/assetdetails`;

export const apiAseSaveSection = (
  assessmentId: UID,
  sectionId: string,
): string => `${PHPAPI}/ases/assessment/${assessmentId}/section/${sectionId}`;

export const apiAseSaveFields = (assessmentId: UID): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/fields`;

export const apiAseSaveFieldsAddInstances = (assessmentId: UID): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/fields/instances`;

export const apiAseSaveFieldInstanceCheck = (assessmentId: UID): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/fields/instance/check`;

export const apiAseSaveFieldsDeleteLite = (assessmentId: UID): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/fields/liteinstance/delete`;

export const apiAseSaveQuestion = (
  assessmentId: UID,
  fieldInstanceId: string | null,
  questionId: string,
): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/` +
  `${fieldInstanceId ? `instance/${fieldInstanceId}/` : ''}` +
  `question/${questionId}`;

export const apiAseSaveChecklist = (
  assessmentId: UID,
  fieldInstanceId: string | null,
  questionId: string,
): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/` +
  `${fieldInstanceId ? `instance/${fieldInstanceId}/` : ''}` +
  `checklist/${questionId}`;

export const apiAseSaveAction = (
  assessmentId: UID,
  actionId: UID | null,
  doDelete?: boolean,
): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/action` +
  `${actionId ? `/${actionId}` : ''}` +
  `${actionId && doDelete ? '/delete' : ''}`;

export const apiAseSaveControl = (
  assessmentId: UID,
  controlId: UID | null,
  doDelete?: boolean,
): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/control` +
  `${controlId ? `/${controlId}` : ''}` +
  `${controlId && doDelete ? '/delete' : ''}`;

export const apiAseSaveFlagResult = (
  assessmentId: UID,
  flagId: string,
  doDelete?: boolean,
): string =>
  `${PHPAPI}/ases/assessment/${assessmentId}/flag/${flagId}` +
  `${doDelete ? '/delete' : ''}`;

// Reporting

export const apiReportCheckSchedule = (): string =>
  `${PHPAPI}/reports/checkschedule`;

export const apiPagesLoad = (): string => `${PHPAPI}/pages`;
export const apiReportReports = (): string => `${PHPAPI}/reports/reports`;
export const apiReportReport = (id: string): string =>
  `${PHPAPI}/reports/report/${id}`;
export const apiReportDataList = (): string => `${PHPAPI}/reports/data/list`;
export const apiReportDataCsv = (): string => `${PHPAPI}/reports/data/csv`;

// Files

export const apiFilesPreview = (
  id: string,
  context: FilesContext,
  special?: {
    convert:
      | 'svgpdf'
      | 'pdfpage'
      | 'image'
      | 'svg'
      | 'pdf'
      | 'pdfimage'
      | undefined;
    page: number | null;
  },
): string =>
  `${PHPAPI}/files/preview/${id}` +
  `${
    (context.context === FilesContextEnum.localAsset
      ? `/asset/${context.assetId}`
      : '') +
    (context.context === FilesContextEnum.localAssessment ||
    context.context === FilesContextEnum.remoteAssessment
      ? `/assessment/${context.assessmentId}`
      : '') +
    (special?.convert
      ? `?cnvrt=${special.convert}&page=${special.page || 1}`
      : '')
  }`;

export const apiFilesDownload = (id: string, context: FilesContext): string =>
  `${PHPAPI}/files/download/${id}` +
  `${
    (context.context === FilesContextEnum.localAsset
      ? `/asset/${context.assetId}`
      : '') +
    (context.context === FilesContextEnum.localAssessment ||
    context.context === FilesContextEnum.remoteAssessment
      ? `/assessment/${context.assessmentId}`
      : '')
  }`;

/**
 * Engage
 */

const engageUrl = (organisationUid: UID) =>
  `${NODEAPI}/organisation/${organisationUid}/settings/engage`;

export const apiSettingsEngageData = (organisationUid: UID) =>
  engageUrl(organisationUid);

export const apiSettingsEngageAddDocuments = (organisationUid: UID) =>
  `${engageUrl(organisationUid)}/adddocuments`;
export const apiSettingsEngageOrderDocuments = (organisationUid: UID) =>
  `${engageUrl(organisationUid)}/orderdocuments`;
export const apiSettingsEngageRemoveDocument = (
  organisationUid: UID,
  documentId: string,
) => `${engageUrl(organisationUid)}/document/${documentId}/remove`;
export const apiSettingsEngageUpdateDocument = (
  organisationUid: UID,
  documentId: string,
) => `${engageUrl(organisationUid)}/document/${documentId}`;

export const apiSettingsEngageAddElements = (organisationUid: UID) =>
  `${engageUrl(organisationUid)}/addelements`;
export const apiSettingsEngageOrderElements = (organisationUid: UID) =>
  `${engageUrl(organisationUid)}/orderelements`;
export const apiSettingsEngageRemoveElement = (
  organisationUid: UID,
  elementId: string,
) => `${engageUrl(organisationUid)}/element/${elementId}/remove`;
export const apiSettingsEngageUpdateElement = (
  organisationUid: UID,
  elementId: string,
) => `${engageUrl(organisationUid)}/element/${elementId}`;

/**
 * SSO
 */

const ssoSettingsUrl = (organisationUid: UID) =>
  `${NODEAPI}/organisation/${organisationUid}/settings/sso`;

export const apiSettingsSsoData = (organisationUid: UID) =>
  ssoSettingsUrl(organisationUid);

export const apiSettingsSsoUpdate = (organisationUid: UID) =>
  ssoSettingsUrl(organisationUid);

/**
 * API
 */

export const apiSettingsApiData = (organisationUid: UID) =>
  `${NODEAPI}/organisation/${organisationUid}/settings/api`;

export const apiSettingsApiUser = (
  organisationUid: UID,
  id?: UID | null,
  isDelete?: boolean,
) =>
  `${NODEAPI}/organisation/${organisationUid}/settings/api/${
    id ? `user/${id}${isDelete ? '/delete' : ''}` : 'user'
  }`;

export const apiSettingsApiWebhook = (
  organisationUid: UID,
  id?: UID | null,
  isDelete?: boolean,
) =>
  `${NODEAPI}/organisation/${organisationUid}/settings/api/${
    id ? `webhook/${id}${isDelete ? '/delete' : ''}` : 'webhook'
  }`;

export const apiSettingsApiTestWebhook = (
  organisationUid: UID,
  id?: UID | null,
) =>
  `${PHPAPI}/organisation/${organisationUid}/settings/api/${
    id ? `webhook/${id}/test` : 'webhook/test'
  }`;

/**
 * Organisation
 */

export const apiSettingsOrganisationList = () => `${PHPAPI}/organisation/list`;
export const apiSettingsOrganisationBasic = (organisationUid: UID) =>
  `${PHPAPI}/organisation/${organisationUid}/basic`;
export const apiSettingsOrganisationData = (organisationUid: UID) =>
  `${PHPAPI}/organisation/${organisationUid}/data`;
export const apiSettingsOrganisationLogo = (
  organisationUid: UID,
  isDelete?: boolean,
) =>
  `${PHPAPI}/organisation/${organisationUid}/logo` +
  `${isDelete ? '/delete' : ''}`;

/**
 * Engage
 */

function getQueryString(searchParams: URLSearchParams): string {
  const string = searchParams.toString();
  return string ? `?${string}` : '';
}

const EngagePrefix = `${NODEAPI}/blockmanagement`;

export function apiEngageProperties(page: number = 1): string {
  const searchParams = new URLSearchParams();
  if (page > 1) {
    searchParams.set('page', `${page}`);
  }
  return `${EngagePrefix}/properties${getQueryString(searchParams)}`;
}

export function apiEngageFlats(propertyId: string, page: number = 1): string {
  const searchParams = new URLSearchParams();
  if (page > 1) {
    searchParams.set('page', `${page}`);
  }
  return `${EngagePrefix}/properties/${propertyId}/flats${getQueryString(searchParams)}`;
}

export function apiEngageFlat(propertyId: string, flatId: string): string {
  return `${EngagePrefix}/properties/${propertyId}/flats/${flatId}`;
}

export function apiEngageSaveContact<
  EP extends BlockManagerSendCommunicationRequest =
    BlockManagerSendCommunicationRequest,
>(
  propertyId: EP['params']['propertyUid'],
  flatId: EP['params']['flatUid'],
): string {
  return `${EngagePrefix}/properties/${propertyId}/flat/${flatId}/sendcommunication`;
}

export function apiEngageSendCommunication(
  propertyId: string,
  flatId: string,
): string {
  return `${EngagePrefix}/properties/${propertyId}/flat/${flatId}/sendcommunication`;
}
